// Implements: F-DSG-001, F-UIX-008, F-UIX-010
/**
 * The open profile's name and its in-place rename (mockup v3 P5): `PATCH /design/profiles/{id}`,
 * which creates no version (owner decision 2026-10-18).
 *
 * The parent mounts it with `key` = the profile id (checklist 14), so a draft or a refusal of
 * profile A never shows on profile B, and an answer for A that arrives after the person opened B has
 * no container left to write into (the hook writes it under A's own id only). One request at a time:
 * the field is disabled while it runs, and it is sent once (`retry: false`, checklist 13). A name
 * that is blank or unchanged sends nothing. A refusal (the name taken in the scope, a 409) is shown
 * under the input, which keeps what the person typed (F-UIX-008).
 */

import { useState } from "react";
import type { ReactElement } from "react";

import type { ApiError } from "../../../shared/api/ApiError";
import { useTranslation } from "../../../shared/i18n";
import { userMessage } from "../../../shared/ui/molecules/userMessage";
import { ProfileNameField } from "../components/ProfileNameField";
import { useRenameProfile } from "../hooks/useDesignProfiles";

export interface ProfileNameContainerProps {
  readonly profileId: string;
  /** The stored name, as the profile's detail states it now. */
  readonly name: string;
}

export function ProfileNameContainer({ profileId, name }: ProfileNameContainerProps): ReactElement {
  const { t } = useTranslation();
  const rename = useRenameProfile();
  const [draft, setDraft] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [refusal, setRefusal] = useState<ApiError | null>(null);
  // The name the server answered, until the stored detail says it too.
  const [renamed, setRenamed] = useState<string | null>(null);
  const shown = renamed ?? name;

  const close = (): void => {
    setDraft(null);
    setRefusal(null);
  };
  const save = async (): Promise<void> => {
    if (draft === null || pending) return;
    const next = draft.trim();
    if (next === "") return;
    if (next === shown) {
      close();
      return;
    }
    setPending(true);
    setRefusal(null);
    const outcome = await rename(profileId, next);
    setPending(false);
    if (!outcome.ok) {
      setRefusal(outcome.error);
      return;
    }
    setRenamed(outcome.summary?.name ?? next);
    close();
  };

  if (draft === null) {
    return (
      <ProfileNameField
        mode="view"
        name={shown}
        onStart={() => {
          setDraft(shown);
        }}
      />
    );
  }
  return (
    <ProfileNameField
      mode="edit"
      draft={draft}
      onDraftChange={setDraft}
      onSave={() => {
        void save();
      }}
      onCancel={close}
      pending={pending}
      error={
        refusal === null
          ? null
          : refusal.status === 409
            ? t("design.rename.taken")
            : userMessage(refusal, t("design.editor.failed"))
      }
    />
  );
}
